import {
  HttpBaseApi,
  mergePaginationQuery,
  TPaginationQuery,
  TPaginationQueryPartial,
} from "../common.interfaces";
import { instituteListMock } from "../../__mocks__/institute/instituteMock";
import type {
  TCreateInstituteWireRequest,
  TCreateInstituteWireResponse,
  TGetInstituteByIdWire,
  TPaginatedInstitutesWire,
  TGetInstituteWire,
  TPatchInstituteByIdWireResponse,
  TOnboardInstituteWireRequest,
  TOnboardInstituteWireResponse,
} from "./institute.wire.interfaces";
import type {
  TAddress,
  TCreateInstituteRequest,
  TCreateAddressRequest,
  TInstitute,
  TInstituteCreateStatus,
  TOnboardInstituteRequest,
  TUpdateAddressRequest,
  TUpdateInstituteRequest,
} from "./institute.interfaces";
import {
  mapCreateWireResponseToTInstitute,
  mapGetInstituteWireToTInstitute,
  NIL_UUID,
  normalizeGetInstituteByIdWire,
  coerceInstituteMutationWireResponseBody,
  normalizeTInstituteContactEmail,
} from "./wireInstituteMappers";
import { toCreateInstituteWireRequest } from "./toCreateInstituteWireRequest";

const shouldUseMock = (): boolean =>
  import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

const instituteMockToGetWire = (institute: TInstitute): TGetInstituteWire => {
  const valuesPayload = JSON.stringify({
    _adminLegacy: {
      type: institute.type,
      registrationNumber: institute.registrationNumber,
      registrationDate: institute.registrationDate ?? "",
      legalEntity: institute.legalEntity ?? "",
    },
  });

  return {
    id: institute.id,
    name: institute.name,
    status: institute.status,
    type: institute.type,
    registrationNumber: institute.registrationNumber,
    registrationDate: institute.registrationDate ?? null,
    legalEntity: institute.legalEntity ?? null,
    contactInformation: institute.contactInformation
      ? { ...institute.contactInformation }
      : null,
    description: `${institute.name} — ${institute.legalEntity ?? ""}`,
    vision: "",
    mission: "",
    values: valuesPayload,
    owner: {},
    address: institute.addresses?.[0] ? { ...institute.addresses[0] } : {},
    contact: institute.contactInformation
      ? { ...institute.contactInformation }
      : {},
    social_media: {},
    createdBy: {},
    deletedAt: institute.deletedAt,
  };
};

/**
 * GET /v1/institutes — OpenAPI InstitutesController_getAllInstitutes (`page`, `limit` required).
 */
export const listInstitutesWireRequest = (
  httpAPI: HttpBaseApi,
  params: TPaginationQuery,
): Promise<TPaginatedInstitutesWire> => {
  const { page, limit } = params;

  if (shouldUseMock()) {
    const start = (page - 1) * limit;
    const slice = instituteListMock.slice(start, start + limit);
    return Promise.resolve({
      data: slice.map(instituteMockToGetWire),
      count: instituteListMock.length,
    });
  }

  return httpAPI.institute.get<TPaginatedInstitutesWire>("", {
    params: { page, limit },
  });
};

export const getInstitutesRequest = async (
  httpAPI: HttpBaseApi,
  params?: TPaginationQueryPartial,
): Promise<TInstitute[]> => {
  const merged = mergePaginationQuery(params);

  if (shouldUseMock()) {
    const start = (merged.page - 1) * merged.limit;
    return instituteListMock.slice(start, start + merged.limit);
  }

  const wire = await listInstitutesWireRequest(httpAPI, merged);

  return wire.data.map(mapGetInstituteWireToTInstitute);
};

export const createInstituteWireRequest = async (
  httpAPI: HttpBaseApi,
  body: TCreateInstituteWireRequest,
  desiredCreateStatus?: TInstituteCreateStatus,
): Promise<TInstitute> => {
  if (shouldUseMock()) {
    const created = mapCreateWireResponseToTInstitute(
      {} as TCreateInstituteWireResponse,
      body,
      desiredCreateStatus,
    );
    created.id = String(instituteListMock.length + 1);
    instituteListMock.push(created);
    return Promise.resolve(created);
  }

  const raw = await httpAPI.institute.post<TCreateInstituteWireResponse>(
    "",
    body,
  );

  return mapCreateWireResponseToTInstitute(raw, body, desiredCreateStatus);
};

export const createInstituteLegacyRequest = async (
  httpAPI: HttpBaseApi,
  legacy: TCreateInstituteRequest,
  actingUserId: string,
): Promise<TInstitute> => {
  const body = toCreateInstituteWireRequest(legacy, actingUserId);
  let institute = await createInstituteWireRequest(
    httpAPI,
    body,
    legacy.status,
  );

  if (
    !shouldUseMock() &&
    institute.id &&
    institute.id !== NIL_UUID &&
    institute.status !== legacy.status
  ) {
    institute = await updateInstituteRequest(httpAPI, institute.id, {
      status: legacy.status,
    });
  }

  return institute;
};

/**
 * GET /v1/institutes/by-id/{instituteId} — OpenAPI `InstitutesController_getInstituteById`.
 */
export const getInstituteByIdRequest = async (
  httpAPI: HttpBaseApi,
  id: string,
): Promise<TInstitute> => {
  if (shouldUseMock()) {
    const institute = instituteListMock.find((inst) => inst.id === id);
    if (!institute) {
      throw new Error("Institute not found");
    }
    return institute;
  }

  const wire = await httpAPI.institute.get<TGetInstituteByIdWire>(
    `/by-id/${encodeURIComponent(id)}`,
  );
  return mapGetInstituteWireToTInstitute(normalizeGetInstituteByIdWire(wire));
};

export const updateInstituteRequest = (
  httpAPI: HttpBaseApi,
  id: string,
  request: TUpdateInstituteRequest,
): Promise<TInstitute> => {
  if (shouldUseMock()) {
    const index = instituteListMock.findIndex((inst) => inst.id === id);
    if (index === -1) {
      return Promise.reject(new Error("Institute not found"));
    }

    const updatedInstitute: TInstitute = {
      ...instituteListMock[index],
      ...request,
      updatedAt: new Date().toISOString(),
    };

    instituteListMock[index] = updatedInstitute;
    return Promise.resolve(updatedInstitute);
  }

  return httpAPI.institute
    .patch<TInstitute | TPatchInstituteByIdWireResponse>(
      `/by-id/${encodeURIComponent(id)}`,
      request,
    )
    .then((body) => {
      const mapped = coerceInstituteMutationWireResponseBody(body);
      if (mapped != null)
        return normalizeTInstituteContactEmail(mapped);
      return normalizeTInstituteContactEmail(body as TInstitute);
    });
};

export const getAddressesRequest = (
  httpAPI: HttpBaseApi,
  instituteId: string,
): Promise<TAddress[]> => {
  if (shouldUseMock()) {
    const institute = instituteListMock.find(
      (inst) => inst.id === instituteId,
    );

    if (!institute) {
      return Promise.reject(new Error("Institute not found"));
    }

    return Promise.resolve(institute.addresses || []);
  }

  return httpAPI.institute.get<TAddress[]>(
    `/by-id/${encodeURIComponent(instituteId)}/addresses`,
  );
};

export const createAddressRequest = (
  httpAPI: HttpBaseApi,
  request: TCreateAddressRequest,
): Promise<TAddress> => {
  if (shouldUseMock()) {
    const institute = instituteListMock.find(
      (inst) => inst.id === request.instituteId,
    );

    if (!institute) {
      return Promise.reject(new Error("Institute not found"));
    }

    const newAddress: TAddress = {
      id: String(Date.now()),
      ...request,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (request.isPrimary && institute.addresses) {
      institute.addresses = institute.addresses.map((addr) => ({
        ...addr,
        isPrimary: false,
      }));
    }

    if (!institute.addresses) {
      institute.addresses = [];
    }

    institute.addresses.push(newAddress);
    institute.updatedAt = new Date().toISOString();

    return Promise.resolve(newAddress);
  }

  return httpAPI.institute.post<TAddress>(
    `/by-id/${encodeURIComponent(request.instituteId)}/addresses`,
    request,
  );
};

export const updateAddressRequest = (
  httpAPI: HttpBaseApi,
  instituteId: string,
  addressId: string,
  request: TUpdateAddressRequest,
): Promise<TAddress> => {
  if (shouldUseMock()) {
    const institute = instituteListMock.find(
      (inst) => inst.id === instituteId,
    );

    if (!institute || !institute.addresses) {
      return Promise.reject(new Error("Institute or address not found"));
    }

    const addressIndex = institute.addresses.findIndex(
      (addr) => addr.id === addressId,
    );

    if (addressIndex === -1) {
      return Promise.reject(new Error("Address not found"));
    }

    if (request.isPrimary && institute.addresses) {
      institute.addresses = institute.addresses.map((addr) => ({
        ...addr,
        isPrimary: addr.id === addressId,
      }));
    }

    const updatedAddress: TAddress = {
      ...institute.addresses[addressIndex],
      ...request,
      updatedAt: new Date().toISOString(),
    };

    institute.addresses[addressIndex] = updatedAddress;
    institute.updatedAt = new Date().toISOString();

    return Promise.resolve(updatedAddress);
  }

  return httpAPI.institute.patch<TAddress>(
    `/by-id/${encodeURIComponent(instituteId)}/addresses/${encodeURIComponent(addressId)}`,
    request,
  );
};

export const deleteAddressRequest = (
  httpAPI: HttpBaseApi,
  instituteId: string,
  addressId: string,
): Promise<void> => {
  if (shouldUseMock()) {
    const institute = instituteListMock.find(
      (inst) => inst.id === instituteId,
    );

    if (!institute || !institute.addresses) {
      return Promise.reject(new Error("Institute or address not found"));
    }

    const addressIndex = institute.addresses.findIndex(
      (addr) => addr.id === addressId,
    );

    if (addressIndex === -1) {
      return Promise.reject(new Error("Address not found"));
    }

    institute.addresses.splice(addressIndex, 1);
    institute.updatedAt = new Date().toISOString();

    return Promise.resolve(void 0);
  }

  return httpAPI.institute.delete<void>(
    `/by-id/${encodeURIComponent(instituteId)}/addresses/${encodeURIComponent(addressId)}`,
  );
};

export const archiveInstituteRequest = async (
  httpAPI: HttpBaseApi,
  id: string,
): Promise<TInstitute> => {
  if (shouldUseMock()) {
    const index = instituteListMock.findIndex((inst) => inst.id === id);

    if (index === -1) {
      return Promise.reject(new Error("Institute not found"));
    }

    const now = new Date().toISOString();
    const archivedInstitute: TInstitute = {
      ...instituteListMock[index],
      status: "archived",
      deletedAt: now,
      updatedAt: now,
    };

    instituteListMock[index] = archivedInstitute;

    return Promise.resolve(archivedInstitute);
  }

  const wire = await httpAPI.institute.patch<TGetInstituteByIdWire>(
    `/by-id/${encodeURIComponent(id)}/archive`,
    {},
  );
  return mapGetInstituteWireToTInstitute(normalizeGetInstituteByIdWire(wire));
};

export const unarchiveInstituteRequest = async (
  httpAPI: HttpBaseApi,
  id: string,
): Promise<TInstitute> => {
  if (shouldUseMock()) {
    const index = instituteListMock.findIndex((inst) => inst.id === id);

    if (index === -1) {
      return Promise.reject(new Error("Institute not found"));
    }

    const { deletedAt: _omit, ...rest } = instituteListMock[index];
    void _omit;
    const restoredInstitute: TInstitute = {
      ...rest,
      updatedAt: new Date().toISOString(),
    };

    instituteListMock[index] = restoredInstitute;

    return Promise.resolve(restoredInstitute);
  }

  const wire = await httpAPI.institute.patch<TGetInstituteByIdWire>(
    `/by-id/${encodeURIComponent(id)}/unarchive`,
    {},
  );
  return mapGetInstituteWireToTInstitute(normalizeGetInstituteByIdWire(wire));
};

/**
 * POST /v1/institutes/onboard — atomic institute + admin creation.
 */
export const onboardInstituteRequest = async (
  httpAPI: HttpBaseApi,
  request: TOnboardInstituteRequest,
  actingUserId: string,
): Promise<TOnboardInstituteWireResponse> => {
  if (shouldUseMock()) {
    const mockInstitute: TInstitute = {
      id: String(instituteListMock.length + 1),
      name: request.name,
      type: request.type ?? "Other",
      registrationNumber: request.registrationNumber ?? "",
      registrationDate: request.registrationDate,
      legalEntity: request.legalEntity,
      status: request.status ?? "draft",
      configurationCompleteness: 0,
    };
    instituteListMock.push(mockInstitute);
    return Promise.resolve({
      instituteId: mockInstitute.id,
      name: mockInstitute.name,
      status: mockInstitute.status,
      domainSlug: request.domainSlug,
      admin: {
        userId: "mock-user-id",
        adminId: "mock-admin-id",
        userName: request.admin.userName,
        designation: request.admin.designation,
      },
      createdBy: actingUserId,
    });
  }

  const body: TOnboardInstituteWireRequest = {
    name: request.name,
    values: "",
    vision: "",
    mission: "",
    domainSlug: request.domainSlug,
    type: request.type,
    registrationNumber: request.registrationNumber,
    registrationDate: request.registrationDate,
    legalEntity: request.legalEntity,
    status: request.status,
    address: {
      city: request.admin.city,
      state: request.admin.state,
      country: request.admin.country,
      pinCode: request.admin.pinCode,
    },
    contact: {
      email: request.admin.email,
      countryCode: request.admin.countryCode,
      phoneNumber: request.admin.phoneNumber,
      isPhoneVerified: false,
      isEmailVerified: false,
    },
    admin: {
      firstName: request.admin.firstName,
      middleName: request.admin.middleName,
      lastName: request.admin.lastName,
      gender: request.admin.gender,
      userName: request.admin.userName,
      userPassword: request.admin.userPassword,
      designation: request.admin.designation,
      contact: {
        email: request.admin.email,
        countryCode: request.admin.countryCode,
        phoneNumber: request.admin.phoneNumber,
        isPhoneVerified: false,
        isEmailVerified: false,
      },
      address: {
        city: request.admin.city,
        state: request.admin.state,
        country: request.admin.country,
        pinCode: request.admin.pinCode,
      },
    },
  };

  return httpAPI.institute.post<TOnboardInstituteWireResponse>("/onboard", body);
};
